import { parseApiResponse, parseOriginProof, } from "./client-transport-discovery.js";
import { canonicalHttpsOrigin } from "./http-origin.js";
const DEFAULT_TIMEOUT_MS = 5_000;
const ORIGIN_PROOF_PATH = "/.well-known/client-node-origin";
export async function discoverNodeByAddress(rawAddress, options = {}) {
    let origin;
    try {
        origin = normalizeNodeAddress(rawAddress);
    }
    catch (error) {
        // Malformed input never reaches the network at all. There is no dedicated status for
        // that in the 3-way result union, and "invalid" is the closer fit of the two
        // non-"verified" branches — "unreachable-or-untrusted" specifically means "we tried and
        // couldn't tell why", which would misrepresent input we never attempted to dial.
        return { reason: messageFor(error), status: "reached-but-invalid" };
    }
    const fetchImpl = options.fetch ?? globalThis.fetch.bind(globalThis);
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), options.timeoutMs ?? DEFAULT_TIMEOUT_MS);
    let response;
    try {
        response = await fetchImpl(new Request(new URL(ORIGIN_PROOF_PATH, origin), {
            method: "GET",
            signal: controller.signal,
        }));
    }
    catch {
        return { status: "unreachable-or-untrusted" };
    }
    finally {
        clearTimeout(timeout);
    }
    if (!response.ok) {
        return { reason: `Node responded with HTTP ${String(response.status)}`, status: "reached-but-invalid" };
    }
    let body;
    try {
        body = await response.json();
    }
    catch {
        return { reason: "Node origin proof response was not valid JSON", status: "reached-but-invalid" };
    }
    let proof;
    try {
        const envelope = parseApiResponse(body);
        proof = parseOriginProof(envelope.data);
    }
    catch (error) {
        return { reason: messageFor(error), status: "reached-but-invalid" };
    }
    if (proof.origin !== origin) {
        return { reason: "Node origin proof did not match the requested address", status: "reached-but-invalid" };
    }
    return {
        candidate: { address: origin, id: proof.node_id, path: "lan-direct", trustMode: proof.trust_mode },
        status: "verified",
    };
}
/**
 * Coerces a bare IPv4, `.local` hostname, host:port, or `http(s)://` URL into an exact
 * HTTPS origin (default port 443). Throws `TypeError` on anything else — the caller
 * translates that into a typed `"reached-but-invalid"` result rather than propagating it.
 */
function normalizeNodeAddress(rawAddress) {
    const trimmed = rawAddress.trim();
    if (trimmed.length === 0)
        throw new TypeError("Node address must not be empty");
    const hasScheme = /^[a-zA-Z][a-zA-Z0-9+.-]*:\/\//u.test(trimmed);
    let url;
    try {
        url = new URL(hasScheme ? trimmed : `https://${trimmed}`);
    }
    catch {
        throw new TypeError("Node address is not a valid host or URL");
    }
    if (hasScheme && url.protocol !== "https:" && url.protocol !== "http:") {
        throw new TypeError("Node address must use http or https");
    }
    if (url.username !== "" || url.password !== "" || url.hostname === "" ||
        (url.pathname !== "/" && url.pathname !== "") || url.search !== "" || url.hash !== "") {
        throw new TypeError("Node address must be a bare host, optionally with a port");
    }
    const candidate = url.port === "" ? `https://${url.hostname}` : `https://${url.hostname}:${url.port}`;
    return canonicalHttpsOrigin(candidate);
}
function messageFor(error) {
    return error instanceof Error ? error.message : "Node address could not be verified";
}
//# sourceMappingURL=node-address-discovery.js.map